import DataGrid, {
  Column,
  ColumnChooser,
  FilterRow,
  HeaderFilter,
  Item,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GridDetailField,
  GridDetailFieldsGrid,
  GridDetailPopup,
  GridDetailSection,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  createRemoteGridFilterOptionChangedHandler,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey
} from '../opsDataGrid';
import type { DirectorySyncStatus, DirectoryUser, DirectoryUsersLoadRequest } from './types';

export type { DirectoryUser, DirectorySyncStatus, DirectoryUsersLoadRequest } from './types';

export const DIRECTORY_USERS_GRID_SCHEMA_VERSION = 3;

export const DIRECTORY_USERS_BASE_COLUMNS = [
  'displayName',
  'mail',
  'accountEnabled',
  'department',
  'jobTitle',
  'company',
  'officeLocation',
  'employeeId',
  'mobilePhone'
] as const;

export const DIRECTORY_USERS_TENANT_COLUMN = 'tenantDisplayName' as const;

export function directoryUsersGridColumns(showTenantColumn: boolean): string[] {
  return showTenantColumn
    ? [DIRECTORY_USERS_TENANT_COLUMN, ...DIRECTORY_USERS_BASE_COLUMNS]
    : [...DIRECTORY_USERS_BASE_COLUMNS];
}

export interface DirectoryUsersGridProps {
  loadPage: (request: DirectoryUsersLoadRequest) => Promise<{ total: number; items: DirectoryUser[] }>;
  loadSyncStatus: () => Promise<DirectorySyncStatus>;
  showTenantColumn?: boolean;
  showFilterRow?: boolean;
  showHeaderFilter?: boolean;
  pageSize?: number;
  stateStorageKey: string;
  filterEmptyHint?: string;
}

type SortDescriptor = {
  selector?: unknown;
  desc?: boolean;
};

function getFirstSort(sort: unknown): SortDescriptor | null {
  if (!sort) return null;
  if (Array.isArray(sort)) return (sort[0] as SortDescriptor | undefined) ?? null;
  return sort as SortDescriptor;
}

function buildLoadRequest(
  options: {
    skip?: number;
    take?: number;
    searchValue?: unknown;
    filter?: unknown;
    sort?: unknown;
  },
  fallbackTake: number
): DirectoryUsersLoadRequest {
  const sort = getFirstSort(options.sort);
  const selector = typeof sort?.selector === 'string' ? sort.selector : undefined;
  const search = typeof options.searchValue === 'string' ? options.searchValue.trim() : '';
  return {
    skip: typeof options.skip === 'number' ? options.skip : 0,
    take: typeof options.take === 'number' ? options.take : fallbackTake,
    search: search || undefined,
    filter: options.filter ? JSON.stringify(options.filter) : undefined,
    sortBy: selector,
    sortDesc: sort?.desc
  };
}

function hasActiveRemoteFilter(options: {
  searchValue?: unknown;
  filter?: unknown;
}): boolean {
  const search = typeof options.searchValue === 'string' ? options.searchValue.trim() : '';
  if (search) return true;
  if (options.filter == null) return false;
  if (Array.isArray(options.filter)) return options.filter.length > 0;
  return true;
}

function EnabledBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: enabled ? 'var(--ops-status-succeeded-bg, #dcfce7)' : 'var(--ops-border-muted, #e5e7eb)',
        color: enabled ? 'var(--ops-status-succeeded-fg, #166534)' : 'var(--ops-text-muted, #6b7280)'
      }}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}

function formatLastSynced(status: DirectorySyncStatus | null): string {
  if (!status?.lastSyncedAt) return 'Catalog last updated: —';
  const when = new Date(status.lastSyncedAt);
  const label = Number.isNaN(when.getTime())
    ? status.lastSyncedAt
    : when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const run = status.lastRunStatus ? ` (${status.lastRunStatus})` : '';
  return `Catalog last updated: ${label}${run}`;
}

function formatSyncedAt(value: string): string {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  return when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function DirectoryUserDetailView({
  user,
  showTenantColumn
}: {
  user: DirectoryUser;
  showTenantColumn: boolean;
}) {
  return (
    <>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>
          {user.displayName}
        </div>
        <div style={{ marginTop: 10 }}>
          <EnabledBadge enabled={user.accountEnabled} />
        </div>
      </div>

      <GridDetailSection title="Profile">
        <GridDetailFieldsGrid>
          <GridDetailField label="Email" muted={!user.mail}>
            {user.mail ?? '—'}
          </GridDetailField>
          <GridDetailField label="Department" muted={!user.department}>
            {user.department ?? '—'}
          </GridDetailField>
          <GridDetailField label="Job title" muted={!user.jobTitle}>
            {user.jobTitle ?? '—'}
          </GridDetailField>
          <GridDetailField label="Company" muted={!user.company}>
            {user.company ?? '—'}
          </GridDetailField>
          <GridDetailField label="Office" muted={!user.officeLocation}>
            {user.officeLocation ?? '—'}
          </GridDetailField>
          <GridDetailField label="Employee ID" muted={!user.employeeId}>
            {user.employeeId ?? '—'}
          </GridDetailField>
          <GridDetailField label="Mobile" muted={!user.mobilePhone}>
            {user.mobilePhone ?? '—'}
          </GridDetailField>
          {showTenantColumn ? (
            <GridDetailField label="Tenant" muted={!user.tenantDisplayName && !user.tenantId}>
              {user.tenantDisplayName ?? user.tenantId ?? '—'}
            </GridDetailField>
          ) : null}
        </GridDetailFieldsGrid>
      </GridDetailSection>

      <GridDetailSection title="Catalog">
        <div style={{ display: 'grid', gap: '0.65rem' }}>
          <GridDetailField label="Last synced">{formatSyncedAt(user.syncedAt)}</GridDetailField>
          <GridDetailField label="Present in catalog">{user.isPresent ? 'Yes' : 'No'}</GridDetailField>
        </div>
      </GridDetailSection>
    </>
  );
}

export function DirectoryUsersGrid({
  loadPage,
  loadSyncStatus,
  showTenantColumn = false,
  showFilterRow = false,
  showHeaderFilter = true,
  pageSize = 100,
  stateStorageKey,
  filterEmptyHint = 'No employees match the current search. Try clearing the search box or Reset layout.'
}: DirectoryUsersGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [syncStatus, setSyncStatus] = useState<DirectorySyncStatus | null>(null);
  const [viewUser, setViewUser] = useState<DirectoryUser | null>(null);
  const [lastLoadFiltered, setLastLoadFiltered] = useState(false);

  const gridColumns = useMemo(() => directoryUsersGridColumns(showTenantColumn), [showTenantColumn]);
  const showColumnFilters = showFilterRow || showHeaderFilter;

  const refreshSyncStatus = useCallback(async () => {
    try {
      setSyncStatus(await loadSyncStatus());
    } catch {
      setSyncStatus(null);
    }
  }, [loadSyncStatus]);

  useEffect(() => {
    void refreshSyncStatus();
  }, [refreshSyncStatus]);

  const dataSource = useMemo(() => {
    const store = new CustomStore<DirectoryUser, string>({
      key: 'id',
      load: async (options) => {
        setLastLoadFiltered(hasActiveRemoteFilter(options));
        const page = await loadPage(buildLoadRequest(options, pageSize));
        return { data: page.items, totalCount: page.total };
      }
    });
    return new DataSource({ store, pageSize, paginate: true });
  }, [loadPage, pageSize]);

  const reloadAll = useCallback(() => {
    void dataSource.reload();
    void refreshSyncStatus();
  }, [dataSource, refreshSyncStatus]);

  const onRemoteFilterOptionChanged = useMemo(
    () => createRemoteGridFilterOptionChangedHandler(dataSource),
    [dataSource]
  );

  const noDataText = lastLoadFiltered ? filterEmptyHint : 'No data';

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={dataSource}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        wordWrapEnabled
        noDataText={noDataText}
        remoteOperations={{ paging: true, filtering: true, sorting: true }}
        onOptionChanged={onRemoteFilterOptionChanged}
        onRowDblClick={(e) => onGridRowDblClick(e, setViewUser)}
        onKeyDown={(e) => onGridFocusedRowEnter(e, setViewUser)}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={DIRECTORY_USERS_GRID_SCHEMA_VERSION}
          columns={gridColumns}
        />
        <Toolbar>
          <Item location="before">
            <span className="ops-grid-toolbar-meta">{formatLastSynced(syncStatus)}</span>
          </Item>
          <OpsGridToolbarItems
            gridRef={gridRef}
            onRefresh={reloadAll}
            refreshHint="Reload directory"
            export={{
              fileName: 'directory-users-export',
              worksheetName: 'Users',
              hint: 'Export users'
            }}
            resetLayout={{ storageKey: stateStorageKey }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <SearchPanel visible width={220} placeholder="Search…" />
        {showHeaderFilter ? <HeaderFilter visible /> : null}
        {showFilterRow ? <FilterRow visible /> : null}
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={pageSize} />
        <Pager
          visible
          showInfo
          showNavigationButtons
          showPageSizeSelector
          allowedPageSizes={[100, 250, 500, 1000]}
          displayMode="full"
        />
        {showTenantColumn ? (
          <Column
            name={DIRECTORY_USERS_TENANT_COLUMN}
            dataField={DIRECTORY_USERS_TENANT_COLUMN}
            caption="Tenant"
            minWidth={180}
            allowFiltering={showColumnFilters}
            allowHeaderFiltering={false}
            calculateDisplayValue={(row: DirectoryUser) =>
              row.tenantDisplayName ?? row.tenantId ?? ''}
          />
        ) : null}
        <Column name="displayName" dataField="displayName" caption="Name" minWidth={180} sortOrder="asc" allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="mail" dataField="mail" caption="Email" minWidth={200} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column
          name="accountEnabled"
          dataField="accountEnabled"
          caption="Account status"
          dataType="boolean"
          width={130}
          trueText="Enabled"
          falseText="Disabled"
          allowFiltering={showColumnFilters}
          allowHeaderFiltering={showColumnFilters}
          cellRender={(c) => <EnabledBadge enabled={Boolean(c.value)} />}
        />
        <Column name="department" dataField="department" caption="Department" minWidth={140} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="jobTitle" dataField="jobTitle" caption="Job title" minWidth={160} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="company" dataField="company" caption="Company" minWidth={140} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="officeLocation" dataField="officeLocation" caption="Office" minWidth={120} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="employeeId" dataField="employeeId" caption="Employee ID" width={120} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
        <Column name="mobilePhone" dataField="mobilePhone" caption="Mobile" minWidth={130} allowFiltering={showColumnFilters} allowHeaderFiltering={showColumnFilters} />
      </DataGrid>

      <GridDetailPopup
        visible={viewUser !== null}
        title="User details"
        onClose={() => setViewUser(null)}
        width={560}
        sizeStorageKey={opsGridDetailPopupSizeKey(stateStorageKey)}
      >
        {viewUser ? (
          <DirectoryUserDetailView user={viewUser} showTenantColumn={showTenantColumn} />
        ) : null}
      </GridDetailPopup>
    </>
  );
}
